import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import PartnerDetailsDialog from './PartnerDetailsDialog';
import CreateCommunityDialog from './CreateCommunityDialog';
import TemporaryDisableAccessDialog from './TemporaryDisableAccessDialog';
import PartnerStatusChangeDialog from './PartnerStatusChangeDialog';
import PartnerAcceptPlansDialog from './PartnerAcceptPlansDialog';
import PartnerSubscriptionPricingDialog from './PartnerSubscriptionPricingDialog';
import PartnerSubscriptionCell from './PartnerSubscriptionCell';
import PartnerStatusBadge from './PartnerStatusBadge';
import PartnerAddedFromBadge from './PartnerAddedFromBadge';
import PartnerPendingStatusActions from './PartnerPendingStatusActions';
import PartnerLiveOnlyCell from './PartnerLiveOnlyCell';
import toast from 'react-hot-toast';
import PageContainer from '../PageContainer';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../../styles/portalListTable';
import PortalPaginationControls from '../layout/PortalPaginationControls';
import { usePartnersQuery } from '../../api/use-partners-query';
import { useGrantPartnerMutation } from '../../api/use-grant-partner-mutation';
import { useRevokePartnerMutation } from '../../api/use-revoke-partner-mutation';
import { useCreatePartnerCommunityMutation } from '../../api/use-create-partner-community-mutation';
import { useSetPartnerTemporaryDisableMutation } from '../../api/use-set-partner-temporary-disable-mutation';
import { useUpdatePartnerStatusMutation } from '../../api/use-update-partner-status-mutation';
import { useUpdatePartnerSubscriptionPricingMutation } from '../../api/use-update-partner-subscription-pricing-mutation';
import { LIST_ROWS_PER_PAGE, VISIBLE_PAGE_COUNT } from '../../constants';
import {
  formatPartnerEmails,
  getPartnerLoginEmails,
  getPartnerProfileId,
  hasPartnerCommunity,
  isSolePartnerLogin,
} from '../../services/partners';
import { getUserInitial } from '../../utils/userDisplay';
import {
  isPartnerLiveStatus,
  normalizePartnerStatus,
  PARTNER_STATUS_FILTERS,
  PARTNER_STATUS_VALUES,
  resolvePartnerStatus,
} from '../../utils/partnerStatus';
import {
  DEFAULT_PARTNER_ACCEPT_AMOUNT,
  formatAcceptedSubscriptionPlanSummary,
} from '../../utils/partnerAcceptPlans';

const PARTNER_PICKER_LIMIT = 100;

const ADD_MODES = {
  NEW: 'new',
  EXISTING: 'existing',
};

const statusFilterGroupSx = {
  flexWrap: 'wrap',
  gap: 0.75,
  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid',
    borderColor: 'divider !important',
    borderRadius: '20px !important',
    margin: 0,
    px: 1.75,
    py: 0.5,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: 'text.secondary',
    bgcolor: 'background.paper',
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      borderColor: 'primary.main !important',
      '&:hover': {
        bgcolor: 'primary.dark',
      },
    },
    '&:hover': {
      bgcolor: 'action.hover',
    },
  },
};

const emptyForm = () => ({
  mode: ADD_MODES.NEW,
  email: '',
  password: '',
  fullName: '',
  selectedPartner: null,
});

const canCreateCommunity = (partner) =>
  Boolean(
    partner?.fullName?.trim() && partner?.shortDescription?.trim() && partner?.avatarUrl?.trim(),
  );

const PartnerManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(
    () => normalizePartnerStatus(searchParams.get('status')) ?? PARTNER_STATUS_VALUES.LIVE,
  );
  const grantPartnerMutation = useGrantPartnerMutation();
  const revokePartnerMutation = useRevokePartnerMutation();
  const createPartnerCommunityMutation = useCreatePartnerCommunityMutation();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [showAddPartnerPassword, setShowAddPartnerPassword] = useState(false);
  const [loginToRevoke, setLoginToRevoke] = useState(null);
  const [partnerToView, setPartnerToView] = useState(null);
  const [partnerToCreateCommunity, setPartnerToCreateCommunity] = useState(null);
  const [partnerToToggleAccess, setPartnerToToggleAccess] = useState(null);
  const [statusChangeRequest, setStatusChangeRequest] = useState(null);
  const [acceptPlansRequest, setAcceptPlansRequest] = useState(null);
  const [subscriptionPricingRequest, setSubscriptionPricingRequest] = useState(null);
  const setPartnerTemporaryDisableMutation = useSetPartnerTemporaryDisableMutation();
  const updatePartnerStatusMutation = useUpdatePartnerStatusMutation();
  const updatePartnerSubscriptionPricingMutation = useUpdatePartnerSubscriptionPricingMutation();
  const [form, setForm] = useState(emptyForm);

  const limit = LIST_ROWS_PER_PAGE;
  const queryParams = useMemo(
    () => ({ limit, page, status: statusFilter }),
    [limit, page, statusFilter],
  );
  const pickerParams = useMemo(() => ({ limit: PARTNER_PICKER_LIMIT, page: 1 }), []);

  const { data, isLoading, isFetching, error } = usePartnersQuery(queryParams);
  const {
    data: pickerData,
    isLoading: pickerLoading,
    error: pickerError,
  } = usePartnersQuery(pickerParams, {
    enabled: addDialogOpen && form.mode === ADD_MODES.EXISTING,
  });

  const partners = data?.partners ?? [];
  const partnerOptions = pickerData?.partners ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(page, totalPages);
  const isNewMode = form.mode === ADD_MODES.NEW;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    const statusFromUrl = normalizePartnerStatus(searchParams.get('status'));
    const nextStatus = statusFromUrl ?? PARTNER_STATUS_VALUES.LIVE;
    if (nextStatus !== statusFilter) {
      setStatusFilter(nextStatus);
      setPage(1);
    }
  }, [searchParams, statusFilter]);

  const handleStatusFilterChange = (_event, nextValue) => {
    if (nextValue == null) return;
    setStatusFilter(nextValue);
    setPage(1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextValue === PARTNER_STATUS_VALUES.LIVE) {
          next.delete('status');
        } else {
          next.set('status', nextValue);
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleOpenStatusChangeDialog = (partner, nextStatus) => {
    const partnerProfileId = getPartnerProfileId(partner);
    if (!partnerProfileId) {
      toast.error('Partner profile id is missing.');
      return;
    }
    setStatusChangeRequest({ partnerName: partner.fullName, partnerProfileId, nextStatus });
  };

  const handleCloseStatusChangeDialog = () => {
    if (updatePartnerStatusMutation.isPending) return;
    setStatusChangeRequest(null);
  };

  const handleCloseAcceptPlansDialog = () => {
    if (updatePartnerStatusMutation.isPending) return;
    setAcceptPlansRequest(null);
  };

  const handleConfirmStatusChange = async () => {
    if (!statusChangeRequest?.partnerProfileId || !statusChangeRequest?.nextStatus) return;

    if (statusChangeRequest.nextStatus === PARTNER_STATUS_VALUES.LIVE) {
      setAcceptPlansRequest({
        partnerName: statusChangeRequest.partnerName,
        partnerProfileId: statusChangeRequest.partnerProfileId,
      });
      setStatusChangeRequest(null);
      return;
    }

    try {
      const result = await updatePartnerStatusMutation.mutateAsync({
        partnerProfileId: statusChangeRequest.partnerProfileId,
        status: statusChangeRequest.nextStatus,
      });
      toast.success(result.message || 'Partner status updated');
      setStatusChangeRequest(null);
      setPage(1);
    } catch (statusError) {
      toast.error(statusError?.message || 'Failed to update partner status');
    }
  };

  const handleConfirmAcceptWithAmount = async (amount) => {
    if (!acceptPlansRequest?.partnerProfileId) return;

    try {
      const result = await updatePartnerStatusMutation.mutateAsync({
        partnerProfileId: acceptPlansRequest.partnerProfileId,
        status: PARTNER_STATUS_VALUES.LIVE,
        amount,
      });
      const feeSummary = formatAcceptedSubscriptionPlanSummary(result.data?.subscriptionPlan);
      toast.success(
        feeSummary
          ? `${result.message || 'Partner approved'} — ${feeSummary}`
          : result.message || 'Partner approved',
      );
      setAcceptPlansRequest(null);
      setPage(1);
    } catch (statusError) {
      toast.error(statusError?.message || 'Failed to approve partner');
    }
  };

  const handleOpenSubscriptionPricingDialog = (partner) => {
    const partnerProfileId = getPartnerProfileId(partner);
    if (!partnerProfileId) {
      toast.error('Partner profile id is missing.');
      return;
    }
    setSubscriptionPricingRequest({
      partnerName: partner.fullName || formatPartnerEmails(partner),
      partnerProfileId,
      initialAmount: partner.subscriptionPlan?.amount ?? DEFAULT_PARTNER_ACCEPT_AMOUNT,
    });
  };

  const handleCloseSubscriptionPricingDialog = () => {
    if (updatePartnerSubscriptionPricingMutation.isPending) return;
    setSubscriptionPricingRequest(null);
  };

  const handleConfirmSubscriptionPricing = async (amount) => {
    if (!subscriptionPricingRequest?.partnerProfileId) return;

    try {
      const result = await updatePartnerSubscriptionPricingMutation.mutateAsync({
        partnerProfileId: subscriptionPricingRequest.partnerProfileId,
        amount,
      });
      const feeSummary = formatAcceptedSubscriptionPlanSummary(result.data?.subscriptionPlan);
      toast.success(
        feeSummary
          ? `${result.message || 'Portal fee updated'} — ${feeSummary}`
          : result.message || 'Portal fee updated',
      );
      setSubscriptionPricingRequest(null);
    } catch (pricingError) {
      toast.error(pricingError?.message || 'Failed to update portal fee');
    }
  };

  const handleOpenAddDialog = () => {
    setForm(emptyForm());
    setShowAddPartnerPassword(false);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    if (grantPartnerMutation.isPending) return;
    setAddDialogOpen(false);
    setShowAddPartnerPassword(false);
  };

  const handleModeChange = (_event, nextMode) => {
    if (!nextMode || grantPartnerMutation.isPending) return;
    setForm({
      ...emptyForm(),
      mode: nextMode,
    });
  };

  const handleSelectPartner = (_event, selectedPartner) => {
    setForm((prev) => ({
      ...prev,
      selectedPartner,
    }));
  };

  const handleGrantPartner = async (e) => {
    e.preventDefault();

    if (isNewMode) {
      if (!form.fullName.trim()) {
        toast.error('Full name is required for a new partner.');
        return;
      }

      const result = await grantPartnerMutation.mutateAsync({
        email: form.email.trim(),
        password: form.password,
        fullName: form.fullName.trim(),
      });
      toast.success(result.message || 'Partner access granted');
    } else {
      if (!form.selectedPartner?.id) {
        toast.error('Select a partner from the list.');
        return;
      }

      const result = await grantPartnerMutation.mutateAsync({
        partnerId: form.selectedPartner.id,
        email: form.email.trim(),
        password: form.password,
      });
      toast.success(result.message || 'Partner access granted');
    }

    setAddDialogOpen(false);
    setShowAddPartnerPassword(false);
    setPage(1);
  };

  const handleOpenRevokeDialog = (partner, email) => {
    setLoginToRevoke({ partner, email });
  };

  const handleCreateCommunity = (partner) => {
    if (!canCreateCommunity(partner) || hasPartnerCommunity(partner)) return;
    setPartnerToCreateCommunity(partner);
  };

  const handleCloseCreateCommunityDialog = () => {
    if (createPartnerCommunityMutation.isPending) return;
    setPartnerToCreateCommunity(null);
  };

  const handleConfirmCreateCommunity = async (form) => {
    if (!partnerToCreateCommunity?.id) return;

    try {
      const result = await createPartnerCommunityMutation.mutateAsync({
        partnerId: partnerToCreateCommunity.id,
        form,
      });
      toast.success(result.message || 'Community created');
      setPartnerToCreateCommunity(null);
    } catch (createError) {
      toast.error(createError?.message || 'Failed to create community');
    }
  };

  const handleCloseRevokeDialog = () => {
    if (revokePartnerMutation.isPending) return;
    setLoginToRevoke(null);
  };

  const handleConfirmRevoke = async () => {
    if (!loginToRevoke?.partner?.id) return;

    const removeWholePartner = isSolePartnerLogin(loginToRevoke.partner);
    if (!removeWholePartner && !loginToRevoke.email) return;

    const result = await revokePartnerMutation.mutateAsync({
      partnerId: loginToRevoke.partner.id,
      email: loginToRevoke.email,
      removeWholePartner,
    });
    toast.success(
      result.message || (removeWholePartner ? 'Partner access revoked' : 'Partner login removed'),
    );
    setLoginToRevoke(null);
  };

  const handleOpenTemporaryAccessDialog = (partner) => {
    setPartnerToToggleAccess(partner);
  };

  const handleCloseTemporaryAccessDialog = () => {
    if (setPartnerTemporaryDisableMutation.isPending) return;
    setPartnerToToggleAccess(null);
  };

  const handleConfirmTemporaryAccess = async () => {
    if (!partnerToToggleAccess?.id) return;

    const nextDisabled = !partnerToToggleAccess.temporaryDisable;

    try {
      const result = await setPartnerTemporaryDisableMutation.mutateAsync({
        partnerId: partnerToToggleAccess.id,
        temporaryDisable: nextDisabled,
      });
      toast.success(
        result.message ||
          (nextDisabled
            ? 'Partner organization temporarily disabled'
            : 'Partner organization enabled'),
      );
      setPartnerToToggleAccess(null);
    } catch (toggleError) {
      toast.error(
        toggleError?.message ||
          (nextDisabled
            ? 'Failed to disable partner organization'
            : 'Failed to enable partner organization'),
      );
    }
  };

  const isRevokingWholePartner = loginToRevoke ? isSolePartnerLogin(loginToRevoke.partner) : false;

  const hasCredentials = form.email.trim() && form.password.length >= 6;
  const canSubmitNew = hasCredentials && form.fullName.trim();
  const canSubmitExisting = hasCredentials && Boolean(form.selectedPartner?.id);
  const canSubmit = isNewMode ? canSubmitNew : canSubmitExisting;

  return (
    <PageContainer title="Partner Management">
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            Status
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            aria-label="Filter partners by status"
            sx={statusFilterGroupSx}
          >
            {PARTNER_STATUS_FILTERS.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Button
          variant="contained"
          color="primary"
          startIcon={<HandshakeIcon />}
          onClick={handleOpenAddDialog}
        >
          Add partner
        </Button>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress color="primary" />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2">
          {error.message}
        </Typography>
      )}

      {!isLoading && !error && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            <TableContainer
              sx={{
                maxHeight: 'calc(100vh - 320px)',
                minHeight: 0,
                overflow: 'auto',
              }}
            >
              <Table stickyHeader>
                <PortalTableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Community</TableCell>
                    <TableCell align="center">Temporary Disable Access</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </PortalTableHead>
                <TableBody>
                  {partners.length === 0 ? (
                    <PortalTableRow>
                      <PortalTableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary">
                          No partners found.
                        </Typography>
                      </PortalTableCell>
                    </PortalTableRow>
                  ) : (
                    partners.filter(Boolean).map((partner) => {
                      const loginEmails = getPartnerLoginEmails(partner);
                      const createCommunityEnabled = canCreateCommunity(partner);
                      const partnerHasCommunity = hasPartnerCommunity(partner);
                      const partnerStatus = resolvePartnerStatus(partner, statusFilter);
                      const isLive = isPartnerLiveStatus(partnerStatus);
                      const isPending = partnerStatus === PARTNER_STATUS_VALUES.PENDING;
                      const partnerName = partner.fullName || formatPartnerEmails(partner);

                      return (
                        <PortalTableRow key={partner.id || partner.email || partner.emails?.[0]}>
                          <PortalTableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                src={partner.avatarUrl || undefined}
                                alt={partner.fullName || formatPartnerEmails(partner)}
                              >
                                {getUserInitial(partner.fullName, formatPartnerEmails(partner))}
                              </Avatar>
                              {partner.fullName ? (
                                <Typography
                                  component="button"
                                  type="button"
                                  variant="body2"
                                  onClick={() => setPartnerToView(partner)}
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    p: 0,
                                    border: 0,
                                    bgcolor: 'transparent',
                                    textAlign: 'left',
                                    font: 'inherit',
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    '&:hover': { color: 'text.primary', opacity: 0.8 },
                                  }}
                                >
                                  {partner.fullName}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                                  —
                                </Typography>
                              )}
                            </Box>
                          </PortalTableCell>
                          <PortalTableCell>
                            {loginEmails.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {loginEmails.map((email) => (
                                  <Box
                                    key={email}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      minHeight: 40,
                                    }}
                                  >
                                    <Typography variant="body2">{email}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              '—'
                            )}
                          </PortalTableCell>
                          <PortalTableCell>
                            <PartnerAddedFromBadge addedFrom={partner.addedFrom} />
                          </PortalTableCell>
                          <PortalTableCell sx={{ whiteSpace: 'normal' }}>
                            <PartnerSubscriptionCell
                              partner={partner}
                              onEditAmount={handleOpenSubscriptionPricingDialog}
                              editDisabled={updatePartnerSubscriptionPricingMutation.isPending}
                            />
                          </PortalTableCell>
                          <PortalTableCell>
                            {isPending ? (
                              <PartnerPendingStatusActions
                                partnerName={partnerName}
                                disabled={updatePartnerStatusMutation.isPending}
                                onStatusChange={(nextStatus) =>
                                  handleOpenStatusChangeDialog(partner, nextStatus)
                                }
                              />
                            ) : (
                              <PartnerStatusBadge status={partnerStatus} />
                            )}
                          </PortalTableCell>
                          <PartnerLiveOnlyCell isLive={isLive} align="center">
                            {partnerHasCommunity ? (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  color: 'success.main',
                                }}
                              >
                                <GroupsIcon fontSize="small" />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: 'inherit' }}
                                >
                                  Community Created
                                </Typography>
                              </Box>
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<GroupsIcon fontSize="small" />}
                                disabled={
                                  !createCommunityEnabled ||
                                  createPartnerCommunityMutation.isPending
                                }
                                onClick={() => handleCreateCommunity(partner)}
                                sx={{ textTransform: 'none', minWidth: 150 }}
                              >
                                Create Community
                              </Button>
                            )}
                          </PartnerLiveOnlyCell>
                          <PartnerLiveOnlyCell isLive={isLive} align="center">
                            {partner.temporaryDisable ? (
                              <Tooltip title="Restore access">
                                <IconButton
                                  aria-label={`Restore access for ${partnerName}`}
                                  onClick={() => handleOpenTemporaryAccessDialog(partner)}
                                  disabled={setPartnerTemporaryDisableMutation.isPending}
                                  size="small"
                                  sx={{ color: 'success.main' }}
                                >
                                  <RestoreIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Temporarily disable access">
                                <IconButton
                                  aria-label={`Temporarily disable access for ${partnerName}`}
                                  onClick={() => handleOpenTemporaryAccessDialog(partner)}
                                  disabled={setPartnerTemporaryDisableMutation.isPending}
                                  size="small"
                                  color="error"
                                >
                                  <BlockIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </PartnerLiveOnlyCell>
                          <PartnerLiveOnlyCell isLive={isLive} align="right">
                            {loginEmails.length > 0 ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                  alignItems: 'flex-end',
                                }}
                              >
                                {loginEmails.map((email) => (
                                  <Box
                                    key={email}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      minHeight: 40,
                                    }}
                                  >
                                    <IconButton
                                      aria-label={`Remove login for ${email}`}
                                      onClick={() => handleOpenRevokeDialog(partner, email)}
                                      disabled={revokePartnerMutation.isPending}
                                      color="error"
                                      size="small"
                                    >
                                      <PersonRemoveIcon />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            ) : null}
                          </PartnerLiveOnlyCell>
                        </PortalTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {total > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderTopColor: 'divider',
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }}
              >
                <PortalPaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  visibleCount={VISIBLE_PAGE_COUNT}
                  isLoading={isFetching}
                  onPageChange={setPage}
                />
              </Box>
            )}
          </Box>
        </>
      )}

      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} fullWidth maxWidth="sm">
        <form onSubmit={handleGrantPartner}>
          <DialogTitle>Add partner</DialogTitle>
          <DialogContent>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={form.mode}
              onChange={handleModeChange}
              disabled={grantPartnerMutation.isPending}
              sx={{ mb: 2 }}
            >
              <ToggleButton value={ADD_MODES.NEW} sx={{ textTransform: 'none', fontWeight: 600 }}>
                New Partner
              </ToggleButton>
              <ToggleButton
                value={ADD_MODES.EXISTING}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Existing Partner
              </ToggleButton>
            </ToggleButtonGroup>

            {!isNewMode && (
              <Autocomplete
                options={partnerOptions.filter(Boolean)}
                loading={pickerLoading}
                disabled={grantPartnerMutation.isPending}
                value={form.selectedPartner}
                onChange={handleSelectPartner}
                getOptionLabel={(option) => (option?.fullName ? option.fullName : '')}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={
                  pickerError
                    ? 'Could not load partners'
                    : pickerLoading
                      ? 'Loading partners…'
                      : 'No partners found'
                }
                renderInput={(params) => (
                  <TextField {...params} margin="dense" label="Select partner" required autoFocus />
                )}
                renderOption={(props, option) => {
                  if (!option) return null;
                  return (
                    <Box component="li" {...props} key={option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar src={option.avatarUrl || undefined} sx={{ width: 32, height: 32 }}>
                          {getUserInitial(option.fullName)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.fullName || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                sx={{ mb: 1 }}
              />
            )}

            <TextField
              autoFocus={isNewMode}
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              required
              disabled={grantPartnerMutation.isPending}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Password"
              type={showAddPartnerPassword ? 'text' : 'password'}
              fullWidth
              required
              disabled={grantPartnerMutation.isPending}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              inputProps={{ minLength: 6 }}
              helperText="Minimum 6 characters — used for partner portal sign-in"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showAddPartnerPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowAddPartnerPassword((prev) => !prev)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                      size="small"
                      disabled={grantPartnerMutation.isPending}
                      sx={{ color: 'text.disabled' }}
                    >
                      {showAddPartnerPassword ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {isNewMode && (
              <TextField
                margin="dense"
                label="Full name"
                fullWidth
                required
                disabled={grantPartnerMutation.isPending}
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={grantPartnerMutation.isPending || !canSubmit}
            >
              {grantPartnerMutation.isPending ? 'Adding…' : 'Add partner'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <PartnerDetailsDialog
        open={Boolean(partnerToView)}
        partner={partnerToView}
        onClose={() => setPartnerToView(null)}
      />

      <CreateCommunityDialog
        open={Boolean(partnerToCreateCommunity)}
        partner={partnerToCreateCommunity}
        submitting={createPartnerCommunityMutation.isPending}
        onClose={handleCloseCreateCommunityDialog}
        onSubmit={handleConfirmCreateCommunity}
      />

      <TemporaryDisableAccessDialog
        open={Boolean(partnerToToggleAccess)}
        partnerName={partnerToToggleAccess?.fullName}
        isCurrentlyDisabled={Boolean(partnerToToggleAccess?.temporaryDisable)}
        submitting={setPartnerTemporaryDisableMutation.isPending}
        onClose={handleCloseTemporaryAccessDialog}
        onConfirm={handleConfirmTemporaryAccess}
      />

      <PartnerAcceptPlansDialog
        open={Boolean(acceptPlansRequest)}
        partnerName={acceptPlansRequest?.partnerName}
        submitting={updatePartnerStatusMutation.isPending}
        onClose={handleCloseAcceptPlansDialog}
        onConfirm={handleConfirmAcceptWithAmount}
      />

      <PartnerSubscriptionPricingDialog
        open={Boolean(subscriptionPricingRequest)}
        partnerName={subscriptionPricingRequest?.partnerName}
        initialAmount={subscriptionPricingRequest?.initialAmount}
        submitting={updatePartnerSubscriptionPricingMutation.isPending}
        onClose={handleCloseSubscriptionPricingDialog}
        onConfirm={handleConfirmSubscriptionPricing}
      />

      <PartnerStatusChangeDialog
        open={Boolean(statusChangeRequest)}
        partnerName={statusChangeRequest?.partnerName}
        nextStatus={statusChangeRequest?.nextStatus}
        submitting={updatePartnerStatusMutation.isPending}
        onClose={handleCloseStatusChangeDialog}
        onConfirm={handleConfirmStatusChange}
      />

      <Dialog
        open={Boolean(loginToRevoke)}
        onClose={handleCloseRevokeDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            maxWidth: 400,
            width: '100%',
          },
        }}
      >
        <DialogContent sx={{ pb: 1, pt: 1 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: '#fce8e6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <PersonRemoveIcon sx={{ fontSize: 32, color: '#f44336' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {isRevokingWholePartner ? 'Remove partner access?' : 'Remove partner login?'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {isRevokingWholePartner ? (
              <>
                Are you sure you want to remove partner access for{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {loginToRevoke?.partner?.fullName || loginToRevoke?.email || 'this partner'}
                </Box>
                ? This will remove the whole partner organization.
              </>
            ) : (
              <>
                Are you sure you want to remove login access for{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {loginToRevoke?.email || 'this email'}
                </Box>
                {loginToRevoke?.partner?.fullName ? (
                  <>
                    {' '}
                    under{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {loginToRevoke.partner.fullName}
                    </Box>
                  </>
                ) : null}
                ?
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 1, pt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCloseRevokeDialog}
            disabled={revokePartnerMutation.isPending}
            sx={{ minWidth: 100, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRevoke}
            disabled={revokePartnerMutation.isPending}
            sx={{ minWidth: 100, textTransform: 'none' }}
          >
            {revokePartnerMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default PartnerManagement;
